import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Autocomplete, Box, CircularProgress, TextField, Typography} from '@mui/material';
import {debounce} from '@mui/material/utils';
import PropTypes from 'prop-types';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import {useLazyGetAllEmployeeProfilesQuery} from '../../../../../api/services/employeeService';

const PAGE_SIZE = 20;

const getEmployeeLabel = (employee) => {
    if (!employee) return '';
    if (employee.description) return employee.description;
    if (employee.label) return employee.label;
    return `${employee.preferredForename ?? ''} ${employee.preferredSurname ?? ''}`.trim();
};

const normalizeEmployeeValue = (employee) => {
    if (!employee?.id) return null;

    return {
        id: employee.id,
        description: getEmployeeLabel(employee),
    };
};

const isSameEmployee = (a, b) => {
    if (!a?.id || !b?.id) return false;
    return String(a.id) === String(b.id);
};

const EmployeeAutocomplete = memo(function EmployeeAutocomplete({
    componentId,
    value,
    onChange,
    readOnly,
    error,
}) {
    const [fetchEmployees] = useLazyGetAllEmployeeProfilesQuery();

    const [selected, setSelected] = useState(() => (value?.id ? value : null));
    const [options, setOptions] = useState([]);
    const [open, setOpen] = useState(false);
    const [cursorId, setCursorId] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);

    const onChangeRef = useRef(onChange);
    const fetchEmployeesRef = useRef(fetchEmployees);
    const searchTermRef = useRef('');
    const requestIdRef = useRef(0);
    const lastSyncedValueIdRef = useRef(value?.id ?? null);

    onChangeRef.current = onChange;
    fetchEmployeesRef.current = fetchEmployees;

    useEffect(() => {
        const nextId = value?.id ?? null;
        const lastId = lastSyncedValueIdRef.current;

        if (String(nextId ?? '') === String(lastId ?? '')) {
            return;
        }

        lastSyncedValueIdRef.current = nextId;
        setSelected(value?.id ? value : null);
    }, [value]);

    const loadEmployees = useMemo(
        () =>
            debounce(async ({searchTerm, cursor, requestId}) => {
                setIsLoadingOptions(true);

                try {
                    const result = await fetchEmployeesRef.current({
                        body: {
                            searchTerms: {
                                fullName: searchTerm,
                            },
                            cursorId: cursor,
                            pageSize: PAGE_SIZE,
                        },
                    }).unwrap();

                    if (requestId !== requestIdRef.current) {
                        return;
                    }

                    const items = Array.isArray(result) ? result : [];

                    if (cursor === 0) {
                        setOptions(items);
                    } else {
                        setOptions((prev) => [...prev, ...items]);
                    }

                    setHasMore(items.length === PAGE_SIZE);
                } catch {
                    if (requestId === requestIdRef.current && cursor === 0) {
                        setOptions([]);
                    }
                } finally {
                    if (requestId === requestIdRef.current) {
                        setIsLoadingOptions(false);
                    }
                }
            }, 300),
        []
    );

    useEffect(() => () => loadEmployees.clear(), [loadEmployees]);

    const requestSearch = useCallback((searchTerm, cursor) => {
        const requestId = ++requestIdRef.current;

        loadEmployees({
            searchTerm,
            cursor,
            requestId,
        });
    }, [loadEmployees]);

    useEffect(() => {
        if (!open) {
            return;
        }

        requestSearch(searchTermRef.current, cursorId);
    }, [open, cursorId, requestSearch]);

    const displayOptions = useMemo(() => {
        if (!selected?.id) {
            return options;
        }

        if (options.some((option) => isSameEmployee(option, selected))) {
            return options;
        }

        return [selected, ...options];
    }, [options, selected]);

    const emitChange = useCallback((nextValue) => {
        const normalized = normalizeEmployeeValue(nextValue);

        lastSyncedValueIdRef.current = normalized?.id ?? null;
        setSelected(normalized);

        onChangeRef.current({
            id: componentId,
            radioValue: normalized,
        });
    }, [componentId]);

    const handleOpen = useCallback(() => {
        searchTermRef.current = '';
        setCursorId(0);
        setHasMore(true);
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        searchTermRef.current = '';
        requestIdRef.current += 1;
        loadEmployees.clear();
        setIsLoadingOptions(false);
    }, [loadEmployees]);

    const handleInputChange = useCallback((_event, newInputValue, reason) => {
        if (reason !== 'input') {
            return;
        }

        searchTermRef.current = newInputValue;
        setHasMore(true);
        setOptions([]);

        if (cursorId !== 0) {
            setCursorId(0);
        } else {
            requestSearch(newInputValue, 0);
        }
    }, [cursorId, requestSearch]);

    const handleChange = useCallback((_event, newValue) => {
        if (readOnly) {
            return;
        }

        emitChange(newValue);
    }, [emitChange, readOnly]);

    const handleScroll = useCallback((event) => {
        const node = event.currentTarget;

        if (
            isLoadingOptions ||
            !hasMore ||
            node.scrollTop + node.clientHeight < node.scrollHeight - 50
        ) {
            return;
        }

        const lastOption = options[options.length - 1];

        if (!lastOption?.id) {
            return;
        }

        setCursorId(lastOption.id);
    }, [hasMore, isLoadingOptions, options]);

    return (
        <Autocomplete
            open={open}
            value={selected}
            options={displayOptions}
            loading={isLoadingOptions}
            disabled={readOnly}
            readOnly={readOnly}
            disablePortal
            clearOnBlur={false}
            filterOptions={(x) => x}
            getOptionLabel={getEmployeeLabel}
            isOptionEqualToValue={isSameEmployee}
            onOpen={handleOpen}
            onClose={handleClose}
            onChange={handleChange}
            onInputChange={handleInputChange}
            noOptionsText={isLoadingOptions ? 'Loading…' : 'No employees found'}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Search employees"
                    error={!!error}
                    helperText={error}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoadingOptions ? <CircularProgress color="inherit" size={20}/> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            ListboxProps={{
                sx: {maxHeight: '350px'},
                onScroll: handleScroll,
            }}
        />
    );
});

EmployeeAutocomplete.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        description: PropTypes.string,
        label: PropTypes.string,
    }),
    onChange: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    error: PropTypes.string,
};

const EmployeeSelect = ({
                            index,
                            componentId,
                            value,
                            onChange,
                            descriptionHtml,
                            onDescriptionHtmlChange,
                            commitDescriptionHtml,
                            enableValidation,
                            isEditMode,
                            isFormArchived,
                            accessLevel,
                            readOnly,
                            placeholderValues,
                            error,
                            audience,
                            recipientType,
                            linkedToStudents,
                            submitMode,
                        }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <RichEditor
                            onChange={onChange}
                            isEditMode={isEditMode}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            placeholderValues={placeholderValues}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                        <RequiredCheckbox
                            enableValidation={enableValidation}
                            index={index}
                            onChange={onChange}
                            isFormArchived={isFormArchived}
                        />
                    </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            error={!!error}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            submitMode={submitMode}
                        />
                        {readOnly
                            ? (
                                <Typography>{getEmployeeLabel(value)}</Typography>
                            )
                            : (
                                <EmployeeAutocomplete
                                    componentId={componentId}
                                    value={value}
                                    onChange={onChange}
                                    readOnly={readOnly}
                                    error={error}
                                />
                            )}
                    </>
                )}
        </Box>
    );
};

EmployeeSelect.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        description: PropTypes.string,
        label: PropTypes.string,
    }),
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    submitMode: PropTypes.bool,
};

export default EmployeeSelect;
